import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useListImagesQuery } from '../../../api/image';
import { selectImageListVersion } from '../../../Store/imageSlice';
import { clearDoneByServerIds } from '../../../Store/imageQueueSlice';
import { RootState } from '../../../Store/store';
import { useCappedPolling } from '../../../hooks/useCappedPolling';
import { appendNew, mergeFirstPage } from './imageListMerge';

interface Image {
    id: string;
    name: string;
    description: string;
    created_by: string;
    owner_id: string;
    s3_path: string;
    status: string;
    tags: string;
    upload_status: string;
    tile_status: string;
    tile_status_reason?: string;
    tile_status_category?: string;
    updated_at: string;
}

// The API only lists an image once an S3-event lambda has flipped its
// upload_status to 'Uploaded', which lags the browser's PUT by seconds to
// minutes, so a single refetch on upload completion sees nothing. Poll instead,
// and only while a row is still on its way in or still being tiled.
// POLL_WINDOW_MS caps it so a stuck server-side pipeline can't poll forever.
const POLL_MS = 10000;
const POLL_WINDOW_MS = 15 * 60 * 1000;

interface UseImageListOptions {
    projectId: string;
    autoFetchAll?: boolean; // If true, automatically fetches all pages until cursor is null
}

interface UseImageListReturn {
    images: Image[];
    isLoading: boolean;
    isLoadingNext: boolean;
    isError: boolean;
    hasMore: boolean;
    fetchNext: () => void;
    refresh: () => void;
    totalCount: number;
    // rows the server has actually returned, excluding pending stand-ins — use
    // this for "is this project empty", not images.length
    serverImageCount: number;
}

export const useImageList = ({ 
    projectId, 
    autoFetchAll = false 
}: UseImageListOptions): UseImageListReturn => {
    const dispatch = useDispatch();
    const listVersion = useSelector(selectImageListVersion);
    const doneUploads = useSelector((state: RootState) => state.imageQueue.done);
    const lastVersionRef = useRef(listVersion);
    const [allImages, setAllImages] = useState<Image[]>([]);
    const [currentCursor, setCurrentCursor] = useState<string | null>(null);
    const [isComplete, setIsComplete] = useState(false);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    
    // Use refs to avoid dependency issues
    const isFetching = useRef(false);
    const isFetchingNext = useRef(false);
    const processedCursorsRef = useRef<Set<string>>(new Set(['initial']));

    // A finished upload we have not seen land yet, or a row still being tiled,
    // are the only two reasons to keep asking the server.
    const listedIds = useMemo(() => new Set(allImages.map((img) => img.id)), [allImages]);
    const awaitedUploads = useMemo(
        () => doneUploads.filter((img) => img.projectId === projectId && !!img.serverId && !listedIds.has(img.serverId!)),
        [doneUploads, projectId, listedIds]
    );
    const preprocessing = useMemo(
        () => allImages.some((img) => img.tile_status === 'Pending' || img.tile_status === 'In Progress'),
        [allImages]
    );

    // Identifies what we are waiting on, so a later upload restarts the give-up
    // window instead of inheriting an already-expired one from an earlier stall.
    const pollReason = useMemo(
        () => awaitedUploads.map((img) => img.serverId).sort().join(',') + (preprocessing ? '|tiling' : ''),
        [awaitedUploads, preprocessing]
    );
    const pollingInterval = useCappedPolling(
        awaitedUploads.length > 0 || preprocessing,
        pollReason,
        POLL_MS,
        POLL_WINDOW_MS
    );

    // GET /image filters on upload_status === 'Uploaded', and the server only sets
    // that once its tiling pipeline runs — minutes after the browser's PUT
    // finishes. Refetching cannot surface the row before then, so stand in for it:
    // the client knows the upload succeeded and which id it got. The real row
    // replaces this one as soon as the server starts returning it.
    //
    // Tied to the poll window on purpose: once we have stopped asking, a
    // stand-in can never be resolved, so leaving it on screen would strand an
    // unclickable ghost row (the image was deleted, or tiling died) that the
    // user has no way to clear.
    const pendingRows = useMemo<Image[]>(
        () =>
            pollingInterval === 0
                ? []
                : awaitedUploads.map((img) => ({
                      id: img.serverId!,
                      name: img.name,
                      description: '',
                      created_by: '',
                      owner_id: '',
                      s3_path: '',
                      status: '',
                      tags: '',
                      upload_status: 'Uploaded',
                      // renders as "Preprocessing", which also keeps the row unclickable
                      tile_status: 'In Progress',
                      updated_at: new Date(img.timeUpload ?? img.lastModified).toISOString(),
                  })),
        [awaitedUploads, pollingInterval]
    );

    const {
        data: apiData,
        isLoading: queryLoading,
        isError: queryError,
        refetch
    } = useListImagesQuery({
        projectId,
        cursor: currentCursor
    });

    // Page 1 stays subscribed even while the user has paginated, so uploads,
    // deletes and status changes still reach the list. Identical args when
    // cursor is null, so this shares the one cache entry instead of firing a
    // second request.
    const { data: firstPage } = useListImagesQuery({ projectId, cursor: null }, { pollingInterval });

    useEffect(() => {
        if (!firstPage?.images) return;
        // a null next_cursor means the server returned nothing at all, so the
        // list really is empty and the old rows must go
        setAllImages((prev) =>
            mergeFirstPage(prev, firstPage.images, firstPage.pagination?.next_cursor == null)
        );
    }, [firstPage]);

    // Once the server returns a row we were standing in for, retire the queue
    // entry — otherwise it keeps a stand-in alive and the queue grows forever.
    useEffect(() => {
        const landed = doneUploads
            .filter((img) => img.serverId && listedIds.has(img.serverId))
            .map((img) => img.serverId!);
        if (landed.length) dispatch(clearDoneByServerIds(landed));
    }, [doneUploads, listedIds, dispatch]);

    useEffect(() => {
        if (apiData && apiData.images.length > 0 && !isFetching.current) {
            isFetching.current = true;
            
            setAllImages(prevImages => appendNew(prevImages, apiData.images));

            const nextCursorValue = apiData.pagination.next_cursor;
            setNextCursor(nextCursorValue);
            
            // Mark current cursor as processed
            const cursorKey = currentCursor || 'initial';
            processedCursorsRef.current.add(cursorKey);
            
            if (nextCursorValue) {
                if (autoFetchAll && !processedCursorsRef.current.has(nextCursorValue)) {
                    // Automatically fetch next page for autoFetchAll mode
                    setTimeout(() => {
                        setCurrentCursor(nextCursorValue);
                        isFetching.current = false;
                    }, 100); // Small delay to prevent rapid-fire requests
                } else {
                    // For manual mode, reset fetching flag immediately
                    isFetching.current = false;
                }
            } else {
                // No more pages
                setIsComplete(true);
                isFetching.current = false;
            }
        } else if (apiData && apiData.images.length === 0) {
            // Empty response - mark as complete
            setIsComplete(true);
            isFetching.current = false;
        } else if (apiData && isFetching.current) {
            // Reset fetching flag even if no new images (edge case)
            isFetching.current = false;
        }

        // Reset pagination state when data arrives
        if (apiData) {
            isFetchingNext.current = false;
        }
    }, [apiData, autoFetchAll]);

    const fetchNext = useCallback(() => {
        if (nextCursor && !processedCursorsRef.current.has(nextCursor) && !isFetching.current && !isFetchingNext.current) {
            isFetchingNext.current = true;
            setCurrentCursor(nextCursor);
        }
    }, [nextCursor]);

    const refresh = useCallback(() => {
        setAllImages([]);
        setCurrentCursor(null);
        processedCursorsRef.current = new Set(['initial']);
        setIsComplete(false);
        setNextCursor(null);
        isFetching.current = false;
        isFetchingNext.current = false;
        refetch();
    }, [refetch]);

    // external reset signal (e.g. after trashing images): full reset + refetch
    useEffect(() => {
        if (lastVersionRef.current !== listVersion) {
            lastVersionRef.current = listVersion;
            refresh();
        }
    }, [listVersion, refresh]);

    const hasMore = !isComplete && nextCursor !== null && !processedCursorsRef.current.has(nextCursor);
    const isLoading = queryLoading && allImages.length === 0;

    // pending rows are display-only: allImages stays server-truth so merging,
    // dedup and the poll conditions above never see a synthetic row
    const images = pendingRows.length ? [...pendingRows, ...allImages] : allImages;

    return {
        images,
        isLoading,
        isLoadingNext: isFetchingNext.current,
        isError: queryError,
        hasMore,
        fetchNext,
        refresh,
        totalCount: images.length,
        serverImageCount: allImages.length
    };
}; 